import { UserMenu, Logout, RefreshIconButton, useGetIdentity, useAuthState } from 'react-admin';
import { AppBar, Box, Toolbar, MenuItem, ListItemIcon, ListItemText, Divider, Typography } from '@mui/material';
import { Person as PersonIcon, Email as EmailIcon } from '@mui/icons-material';
import { TenantSelector } from './TenantSelector';
import { useBrand } from './ServerBrandProvider';
import { NotificationsSetup } from './NotificationsSetup';

const MyUserMenu = (props: any) => {
  // Filter out props that shouldn't be passed to DOM elements
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { dashboard, ...sanitizedProps } = props;
  // dashboard prop is filtered out to avoid DOM warnings

  const { data: identity } = useGetIdentity();

  return (
    <UserMenu {...sanitizedProps}>
      {identity && (
        <>
          <MenuItem disabled sx={{ opacity: 1, '&.Mui-disabled': { opacity: 1 } }}>
            <Box sx={{ py: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ListItemIcon sx={{ minWidth: 36, color: '#333' }}>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="body2" sx={{ color: '#333', fontWeight: 600 }}>
                  {identity.fullName || identity.name || 'Unknown User'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ListItemIcon sx={{ minWidth: 36, color: '#666' }}>
                  <EmailIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  {identity.email || 'No email'}
                </Typography>
              </Box>
            </Box>
          </MenuItem>
          <Divider />
        </>
      )}
      <Logout />
    </UserMenu>
  );
};

// This creates a custom AppBar without the sidebar toggle button
export const CustomAppBar = (props: any) => {
  // Filter out props that shouldn't be passed to DOM elements
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { dashboard, hidetenantSelector, ...sanitizedProps } = props;
  // dashboard prop is filtered out to avoid DOM warnings
  const { brand } = useBrand();
  const { identity, loading: identityLoading } = useAuthState();

  // Check if user is an enterprise admin
  // Also check the hidetenantSelector prop passed from CustomLayout
  const isEnterpriseAdmin = hidetenantSelector === true || (identity as any)?.isEnterpriseAdmin === true;

  // Default colors to use while branding is loading
  const primaryColor = brand?.colors?.primary || '#667eea';
  const secondaryColor = brand?.colors?.secondary || '#764ba2';

  return (
    <AppBar
      color="primary"
      elevation={0}
      sx={{
        backgroundColor: 'transparent',
        color: primaryColor,
        position: 'relative',
        width: '100%',
        '&.MuiPaper-root': {
          borderBottom: 'none',
          boxShadow: 'none',
        },
      }}
    >
      <Toolbar sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
        {/* Left side - empty for now */}
        <Box>{/* Can add logo or other elements here if needed */}</Box>

        {/* Right side - contains tenant selector, refresh icon and user menu */}
        <Box
          className="ext-app-bar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <NotificationsSetup />

          {/* Hide tenant selector for enterprise admins */}
          {!isEnterpriseAdmin && <TenantSelector />}

          <Box
            className="ext-horizontal-separator"
            sx={{
              width: '1px',
              height: '24px',
              backgroundColor: secondaryColor,
              border: `1px solid ${secondaryColor}`,
              opacity: 0.3,
            }}
          />

          <RefreshIconButton />

          <Box
            className="ext-horizontal-separator"
            sx={{
              width: '1px',
              height: '24px',
              backgroundColor: secondaryColor,
              border: `1px solid ${secondaryColor}`,
              opacity: 0.3,
            }}
          />

          <MyUserMenu {...sanitizedProps} />
        </Box>
      </Toolbar>
    </AppBar>
  );
};
